import React, { Children, cloneElement, useEffect } from 'react';
import { connect } from 'react-redux';
import SideNav from 'pages/studyOverview/components/SideNav';
import lodash, { filter, get } from 'lodash';
import {
    TwoColumnScaffold,
    LeftColumn,
    RightColumn
} from 'pages/studyOverview/components/TwoColumnScaffold';
import { actions, features, canAccessStudyPermissions } from 'common/permissions/studyPredicates';
import { newDoaStudyFlowAccess } from '../../../../common/features/predicates';
import {
    loadExternalUsersForStudy,
    getStudyDescriptors,
    updateStudyDetails,
    getBinderDescriptors,
    checkForEmptyDetails
} from 'pages/studyOverview/actions/studySettings';
import { getDatasourceOverrides } from 'institutionalAdmin/actions/institutions';
import {
    loadDataSourceList,
} from 'pages/dataSource/actions/dataSource';
import {
    getStudyStages,
    getStudyMetadata
} from 'pages/study/actions/createStudyV2';

import { getStudyMetadata as getStudyMetadataV4 } from '../../actions/study';



function StudySettings(props) {
    const study = lodash.get(props, 'study', {});
    const studySettings = lodash.get(props, 'studySettings', {});
    const metadata = lodash.get(study, 'metadata', []);
    const binderMetadata = metadata.filter(data => data.type === 'binder');
    const studyMetadata = metadata.filter(data => data.type === 'study');

    useEffect(() => {
        const { binderId, institutionId, id } = study;
        const { loadExternalUsersForStudy, loadDataSourceList, getDatasourceOverrides, getStudyStages, studyType, getStudyMetadata, getStudyMetadataV4 } = props;
        loadExternalUsersForStudy(binderId, institutionId, {});
        loadDataSourceList(institutionId);
        getDatasourceOverrides(institutionId);
        getStudyStages(id);
        getStudyMetadataV4(id);
        if (studyType && studyType.binderTemplateId && studyType.id) {
            const { id, binderTemplateId } = studyType;
            getStudyMetadata(institutionId, binderTemplateId, id);
        }

        const {
            getBinderDescriptors,
            getStudyDescriptors
        } = props;
        getBinderDescriptors(institutionId, studyType.id);
        getStudyDescriptors(institutionId, studyType.id);
    }, []);

    useEffect(() => {
        const {
            checkForEmptyDetails
        } = props;
        checkForEmptyDetails();
    }, [props.studyDetails, props.metadata]);

    const childElement = cloneElement(Children.only(props.children), {
        study,
        binderMetadata,
        studyMetadata,
        studySettings
    });

    if (props.loadingStudyMonitors) {
        return null;
    }

    const sideNavLinks = buildStudySettingsLinks(study.id, props.studyPermissions, props.isNewDoaEnabled);
    return (
        <TwoColumnScaffold data-cy="StudySettings">
            <LeftColumn>
                <SideNav
                    links={sideNavLinks}
                    location={props.location}
                    activeIndex={() =>
                        lodash.findIndex(
                            sideNavLinks,
                            (link) => props.location.pathname.includes(link.to)
                        )
                    }
                />
            </LeftColumn>
            <RightColumn data-cy="StudySettings-RightColumn">
                {childElement}
            </RightColumn>
        </TwoColumnScaffold>
    );
}


const buildStudySettingsLinks = (studyId, studyPermissions, isNewDoaEnabled) => {
    const canAccessChild = (permissions) => {
        return permissions.reduce(
            (prev, current) => prev || canAccessStudyPermissions(studyPermissions, current.action, current.feature),
            false
        );
    };

    return [
        {
            to: `/study/${studyId}/settings/study`,
            title: 'Basic Information',
            permissions: [
                { action: actions.VIEW, feature: features.METADATA },
                { action: actions.MANAGE, feature: features.METADATA }
            ]
        },
        {
            to: `/study/${studyId}/settings/details`,
            title: 'Details',
            useEmptyDetailIcon: true,
            permissions: [
                { action: actions.VIEW, feature: features.METADATA },
                { action: actions.MANAGE, feature: features.METADATA }
            ]
        },
        {
            to: `/study/${studyId}/settings/expectations`,
            title: 'Expectations',
            permissions: [
                { action: actions.MANAGE, feature: features.EXPECTATIONS }
            ]
        },
    ].filter((studySettingsLink) => {
        if (!isNewDoaEnabled || (isNewDoaEnabled && studySettingsLink.title !== 'Study Users')) {
            if (lodash.isEmpty(studySettingsLink.permissions)) {
                return true;
            }
            return studySettingsLink.children
                ? studySettingsLink.children.length > 0
                : canAccessChild(studySettingsLink.permissions);
        }
    });
};



export default connect((state) => {
    const institutionId = get(state, 'me.institution.id');
    const studyPermissions = lodash.get(state, 'me.studyPermissions', {});
    const loadingStudyMonitors = lodash.get(state, 'externalUsersForStudyLoading', false);
    const isNewDoaEnabled = newDoaStudyFlowAccess(state);
    const studyDetails = lodash.get(state, 'study.studyDetails', []);
    const metadata = lodash.get(state, 'study.metadata', []);

    return {
        institutionId,
        dataSources: filter(state.dataSources, ['institutionId', institutionId]),
        studyPermissions,
        loadingStudyMonitors,
        isNewDoaEnabled,
        studyType: get(state, 'study.studyType', null),
        studyDetails,
        metadata
    };
}, {
    loadExternalUsersForStudy,
    loadDataSourceList,
    getDatasourceOverrides,
    getStudyStages,
    getStudyMetadata,
    getStudyDescriptors,
    updateStudyDetails,
    getBinderDescriptors,
    getStudyMetadataV4,
    checkForEmptyDetails

})(StudySettings);
